import { Directive, HostListener } from '@angular/core';
import * as Sentry from '@sentry/angular';

const MAX_LABEL_ATTR = 200;

function truncate(s: string, max: number): string {
  const t = s.trim();
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, max)}…`;
}

/**
 * Logs button clicks to Sentry Logs (production) as "User Clicked: {info}".
 * Opt out: data-no-click-log on the element.
 */
@Directive({
  selector: 'button:not([data-no-click-log]),input[type=button]:not([data-no-click-log]),input[type=submit]:not([data-no-click-log])',
})
export class ButtonClickLogDirective {
  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (!(event instanceof MouseEvent) || !event.isTrusted) {
      return;
    }
    const el = event.currentTarget as HTMLElement;
    if (!el) {
      return;
    }
    const info = this.resolveInfo(el);
    const labelForAttr = truncate(info, MAX_LABEL_ATTR);
    Sentry.logger.info(`User Clicked: ${info}`, {
      tagName: el.tagName,
      id: el.id || undefined,
      label: labelForAttr,
    });
  }

  private resolveInfo(el: HTMLElement): string {
    const data = el.getAttribute('data-click-info');
    if (data?.trim()) {
      return data.trim();
    }
    const aria = el.getAttribute('aria-label');
    if (aria?.trim()) {
      return aria.trim();
    }
    const text = el.textContent?.trim();
    if (text) {
      return truncate(text, MAX_LABEL_ATTR);
    }
    if (el instanceof HTMLInputElement) {
      const v = el.value?.trim();
      if (v) {
        return truncate(v, MAX_LABEL_ATTR);
      }
    }
    const title = el.getAttribute('title');
    if (title?.trim()) {
      return title.trim();
    }
    return 'button';
  }
}
